import React from 'react';
import { useIntl } from 'react-intl';
import PackageTierComparisonComponent
  from '@fuww/library/src/PackageTierComparison';
import messages from '../../lib/messages.json';

interface FeatureConfig {
  key: string;
  messageId: keyof typeof messages;
  absent?: boolean;
}

interface TierConfig {
  id: string;
  titleKey: keyof typeof messages;
  taglineKey: keyof typeof messages;
  ctaKey: keyof typeof messages;
  features: FeatureConfig[];
  flagship?: boolean;
}

const internships = {
  key: 'internships',
  messageId: 'premiumEmployer.package.feature.unlimitedInternships',
};

const employerPage = {
  key: 'employer-page',
  messageId: 'premiumEmployer.package.feature.employerPage',
};

const advertorials = {
  key: 'advertorials',
  messageId: 'premiumEmployer.package.feature.advertorials',
};

const tiers: TierConfig[] = [
  {
    id: 'essentials',
    titleKey: 'premiumEmployer.package.essentials.title',
    taglineKey: 'premiumEmployer.package.essentials.tagline',
    ctaKey: 'premiumEmployer.package.essentials.cta',
    features: [
      {
        key: 'press-releases',
        messageId: 'premiumEmployer.package.feature.pressReleasesOne',
      },
      internships,
      { ...employerPage, absent: true },
      { ...advertorials, absent: true },
    ],
  },
  {
    id: 'growth',
    titleKey: 'premiumEmployer.package.growth.title',
    taglineKey: 'premiumEmployer.package.growth.tagline',
    ctaKey: 'premiumEmployer.package.growth.cta',
    features: [
      {
        key: 'press-releases',
        messageId: 'premiumEmployer.package.feature.pressReleasesFour',
      },
      internships,
      { ...employerPage, absent: true },
      { ...advertorials, absent: true },
    ],
  },
  {
    id: 'professional',
    titleKey: 'premiumEmployer.package.professional.title',
    taglineKey: 'premiumEmployer.package.professional.tagline',
    ctaKey: 'premiumEmployer.package.professional.cta',
    features: [
      {
        key: 'press-releases',
        messageId: 'premiumEmployer.package.feature.pressReleasesEight',
      },
      internships,
      employerPage,
      { ...advertorials, absent: true },
    ],
  },
  {
    id: 'enterprise',
    titleKey: 'premiumEmployer.package.enterprise.title',
    taglineKey: 'premiumEmployer.package.enterprise.tagline',
    ctaKey: 'premiumEmployer.package.enterprise.cta',
    features: [
      {
        key: 'press-releases',
        messageId: 'premiumEmployer.package.feature.pressReleasesUnlimited',
      },
      internships,
      employerPage,
      advertorials,
    ],
    flagship: true,
  },
];

const jobsEmail = 'jobs@fashionunited.com';

const buildTierMailto = (subject: string, body: string) => {
  const query = `subject=${
    encodeURIComponent(subject)}&body=${
    encodeURIComponent(body.replaceAll(/\r?\n/g, '\r\n'))}`;
  return `mailto:${jobsEmail}?${query}`;
};

const PackageTierComparison = () => {
  const intl = useIntl();

  return (
    <PackageTierComparisonComponent
      ctaHref={`mailto:${jobsEmail}`}
      tiers={tiers.map(({
        id,
        titleKey,
        taglineKey,
        ctaKey,
        features,
        flagship,
      }) => {
        const title = intl.formatMessage(messages[titleKey]);

        return {
          id,
          title,
          tagline: intl.formatMessage(messages[taglineKey]),
          ctaLabel: intl.formatMessage(messages[ctaKey]),
          ctaHref: buildTierMailto(
            intl.formatMessage(
              messages['premiumEmployer.package.emailSubject'],
              { tier: title },
            ),
            intl.formatMessage(
              messages['premiumEmployer.package.emailBody'],
              { tier: title },
            ),
          ),
          features: features.map(({ key, messageId, absent }) => ({
            key,
            label: intl.formatMessage(messages[messageId]),
            ...(absent ? { absent: true } : {}),
          })),
          ...(flagship
            ? {
              flagship: true,
              flagshipBadgeLabel: intl.formatMessage(
                messages['premiumEmployer.package.flagshipBadge'],
              ),
            }
            : {}),
        };
      })}
    />
  );
};

export default PackageTierComparison;
